import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';
import { testimonials } from '@/mocks/home';

const formSubmitAddr = 'https://readdy.ai/api/form/d9u458t3pcjqs2fcslng';

export default function AboutPage() {
  return (
    <main className="pt-20 md:pt-24 pb-0 bg-background-50 min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mb-6">
        <Breadcrumb items={[{ label: '关于我们' }]} />
      </div>

      {/* Brand Intro */}
      <section className="py-10 md:py-16 px-4 md:px-6" id="about">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                {aboutData.brandIntro.title}
              </h1>
              <p className="text-sm md:text-base text-primary-500 font-medium mb-6">
                {aboutData.brandIntro.subtitle}
              </p>
              {aboutData.brandIntro.description.map((paragraph, idx) => (
                <p key={idx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
              <div className="flex flex-wrap gap-3 mt-6">
                <Link
                  to="/category"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  浏览内容
                </Link>
                <a
                  href="#contact"
                  className="px-6 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  联系我们
                </a>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src={aboutData.brandIntro.image}
                alt="蜜桃传媒办公环境"
                className="w-full h-64 md:h-80 object-cover object-top"
                title="蜜桃传媒 - 创意办公空间"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">核心价值</h2>
            <p className="text-sm text-foreground-500 mt-2">四个核心理念驱动蜜桃传媒不断前行</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {aboutData.brandIntro.values.map((value) => (
              <div key={value.title} className="bg-background-50 rounded-lg p-6 md:p-7 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className={`${value.icon} text-2xl text-primary-500`} />
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{value.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">发展历程</h2>
            <p className="text-sm text-foreground-500 mt-2">从创立到领先，每一步都值得铭记</p>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-accent-300/40 md:-translate-x-px" />
            {aboutData.milestones.map((milestone, idx) => (
              <div
                key={milestone.year}
                className={`relative flex items-start gap-6 md:gap-10 mb-8 md:mb-10 ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className="md:w-1/2 md:text-right hidden md:block">
                  {idx % 2 === 0 ? (
                    <div className="pr-10">
                      <span className="text-2xl font-heading font-bold text-accent-500">{milestone.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                    </div>
                  ) : null}
                </div>
                <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-check-line text-background-50 text-xs" />
                </div>
                <div className="md:w-1/2 flex-1">
                  {idx % 2 === 0 ? (
                    <div className="md:hidden">
                      <span className="text-xl font-heading font-bold text-accent-500">{milestone.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                    </div>
                  ) : (
                    <div className="md:pl-10">
                      <span className="text-2xl font-heading font-bold text-accent-500">{milestone.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-200/70">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">荣誉奖项</h2>
            <p className="text-sm text-foreground-500 mt-2">行业认可，实力见证</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {aboutData.awards.map((award) => (
              <div key={award.title} className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-award-line text-xl text-accent-500" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">{award.title}</h3>
                <p className="text-xs text-foreground-500">{award.org}</p>
                <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full bg-secondary-50 text-secondary-600 text-xs font-medium">
                  {award.year}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Testimonials */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">用户评价</h2>
            <p className="text-sm text-foreground-500 mt-2">来自用户的真实反馈</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((item) => (
              <div key={item.name} className="bg-background-100 rounded-lg p-5 md:p-6">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  &ldquo;{item.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover object-top"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{item.name}</div>
                    <div className="text-xs text-foreground-400">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100" id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">常见问题</h2>
            <p className="text-sm text-foreground-500 mt-2">关于蜜桃传媒你可能想了解的事情</p>
          </div>
          <div className="flex flex-col gap-3 md:gap-4">
            {aboutData.faqs.map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-800 list-none">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-200" />
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50" id="contact">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">联系我们</h2>
            <p className="text-sm text-foreground-500 mt-2">有任何问题或合作意向，欢迎联系我们</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10">
            <div className="md:col-span-3">
              <form
                data-readdy-form=""
                id="contact-form"
                action={formSubmitAddr}
                method="POST"
                encType="application/x-www-form-urlencoded"
                className="flex flex-col gap-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const honeypot = (form.querySelector('.form-hp-field') as HTMLInputElement)?.value?.trim();
                  if (honeypot) {
                    const msgEl = document.getElementById('form-status-msg');
                    if (msgEl) {
                      msgEl.textContent = '提交成功，我们会尽快与您联系！';
                      msgEl.className = 'text-sm text-accent-600';
                    }
                    form.reset();
                    return;
                  }
                  const formData = new FormData(form);
                  try {
                    const res = await fetch(formSubmitAddr, {
                      method: 'POST',
                      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    });
                    const responseText = await res.text();
                    let parsed: Record<string, unknown> = {};
                    try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
                    const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed as Record<string, string>)?.message || '';
                    const code = (parsed as Record<string, string>)?.code || '';
                    const msgEl = document.getElementById('form-status-msg');
                    if (msgEl) {
                      if (res.ok && code === 'OK') {
                        msgEl.textContent = '提交成功，我们会尽快与您联系！';
                        msgEl.className = 'text-sm text-accent-600';
                        form.reset();
                      } else {
                        msgEl.textContent = serverMsg || '提交失败，请稍后重试';
                        msgEl.className = 'text-sm text-primary-500';
                      }
                    }
                  } catch {
                    const msgEl = document.getElementById('form-status-msg');
                    if (msgEl) {
                      msgEl.textContent = '网络异常，请稍后重试';
                      msgEl.className = 'text-sm text-primary-500';
                    }
                  }
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      placeholder="请输入您的姓名"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      placeholder="请输入您的邮箱"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    placeholder="请输入主题"
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请输入您的留言（最多500字）"
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  />
                  <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                </div>
                {/* Anti-spam honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  className="form-hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    提交留言
                  </button>
                </div>
                <div id="form-status-msg" className="text-sm" />
              </form>
            </div>

            <div className="md:col-span-2 flex flex-col gap-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg text-primary-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">邮箱</div>
                  <a href={`mailto:${aboutData.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                    {aboutData.contact.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-lg text-accent-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">电话</div>
                  <a href={`tel:${aboutData.contact.phone}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                    {aboutData.contact.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-lg text-secondary-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">地址</div>
                  <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg text-secondary-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">工作时间</div>
                  <p className="text-sm text-foreground-500">{aboutData.contact.workingHours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org structured data for About page */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于蜜桃传媒',
            description: '蜜桃传媒是一家专注优质内容创作与传播的媒体平台。了解我们的品牌故事、核心价值、发展历程和联系方式。',
            about: {
              '@type': 'Organization',
              name: '蜜桃传媒',
              foundingDate: '2020',
              description: '专注优质内容创作与传播的媒体平台',
              contactPoint: {
                '@type': 'ContactPoint',
                email: aboutData.contact.email,
                telephone: aboutData.contact.phone,
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
              address: {
                '@type': 'PostalAddress',
                streetAddress: '望京SOHO T3-A座 25层',
                addressLocality: '北京',
                addressRegion: '朝阳区',
                addressCountry: 'CN',
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: aboutData.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </main>
  );
}